import React from 'react';
import { toJS } from 'mobx';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import EnrollmentTag from 'components/common/enrollment/EnrollmentTag';
import { EnrollmentPeriod, EnrollmentType } from 'types/enrollmentService';
import { useRootStore } from 'store/rootStore';
import { sortByProductLineId } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import FormsSpinner from 'components/common/loaders/FormSpinner';
import { observer } from 'mobx-react-lite';
import StageButton from 'components/common/enrollment/StageButton';
import { Button } from '@insurancemenu/imenu-components-lib';
import { useHistory } from 'react-router-dom';
import { createStyles } from './styles';
import InstalledPortfolioCard from './InstalledPortfolioCard';
import { getMaxRateCount, hasMultiProductsPortfolio } from '../utils';

interface InstalledEnrollmentCardProps {
  enrollmentPeriod: EnrollmentPeriod;
}

const InstalledEnrollmentCard = observer(({ enrollmentPeriod }: InstalledEnrollmentCardProps): JSX.Element => {
  const { id, installedProposal, effectiveDate, hasConfirmedProposal } = enrollmentPeriod;

  const classes = createStyles();
  const history = useHistory();

  // Mobx store
  const {
    userStore: { userRoles },
    enrollmentCardStore: { getPeriodLoading },
  } = useRootStore();

  // Common data
  const convertedUserRoles = userRoles.map((role) => toJS(role)).slice();

  const cardsList = installedProposal?.productLines
    .sort((a, b) => sortByProductLineId(a.id, b.id))
    .filter((productLineData) => productLineData.products.length > 0)
    .map((productLineData) => {
      return (
        <InstalledPortfolioCard
          key={`${id}-${productLineData.id}`}
          productLineData={productLineData}
          maxRatesCount={getMaxRateCount(installedProposal?.productLines || [], EnrollmentType.INSTALLED)}
          hasMultiProductsPortfolio={hasMultiProductsPortfolio(installedProposal)}
          effectiveDate={effectiveDate}
        />
      );
    });

  function onContinueClick(): void {
    history.push(`/checklist/${enrollmentPeriod.id}`);
  }

  return (
    <div className={classes.enrollmentCard}>
      <FormsSpinner display={getPeriodLoading(id)} />
      <header className={classes.cardHeader}>
        <div className={classes.leftHeaderSection}>
          <div>
            <h6 className={classes.effectiveDateTitle}>Renewal Date</h6>
            <h5 className={classes.effectiveDateValue}>{dateFormatters.normDateToShortAmericanDate(effectiveDate)}</h5>
          </div>
          <div className={classes.stageTagContainer}>
            <EnrollmentTag userRoles={convertedUserRoles} enrollmentPeriod={enrollmentPeriod} />
          </div>
        </div>
        <div className={classes.rightHeaderSection}>
          <StageButton enrollmentPeriod={enrollmentPeriod} />
          {hasConfirmedProposal && (
            <Button color="primary" size="small" width="18.4rem" onClick={onContinueClick}>
              Continue
            </Button>
          )}
        </div>
      </header>
      <footer className={classes.footer}>
        {cardsList && cardsList.length > 0 && <div className={classes.portfoliosList}>{cardsList}</div>}
      </footer>
    </div>
  );
});

export default InstalledEnrollmentCard;
